import type React from "react";
import { useState } from "react";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import type { IProModalCustomStyles } from "pro-features/components/Modals/src/ProModal";
import { ProModal } from "pro-features/components/Modals/src/ProModal";
import { Button } from "@similarweb/ui-components";
import type { ButtonTypeProp } from "app/UtilitiesAndConstants/Constants/ButtonTypes";
import {
    StyledTitleContainer,
    StyledDetailsContainer,
    StyledButtonContainer,
    StyledSLModalContainer,
    StyledCancelBtn,
    StyledCheckboxContainer,
} from "./styles";
import { WithKeysInput } from "app/pages/sales-intelligence/sub-modules/management/hocs/withKeysInput";
import { Checkbox } from "@similarweb/ui-components";

type SLModalProps = {
    onConfirm(): void;
    onClose?(): void;
    onCheckboxClick?(state: boolean): void;
    checkboxText?: string;
    isOpen: boolean;
    title: string | React.ReactNode;
    confirmBtnText?: string;
    cancelBtnText?: string;
    details?: string | React.ReactNode;
    confirmBtnType?: ButtonTypeProp;
    cancelBtnType?: ButtonTypeProp;
    customStyles?: IProModalCustomStyles;
    customConfirmStyles?: React.CSSProperties;
    customDetailsStyles?: React.CSSProperties;
    customTitleStyles?: React.CSSProperties;
};

/**
 * Reusable Modal generator component styled for Suppression list
 * @param {function} onClose close modal handler
 * @param {function} onConfirm confirm modal handler
 * @param {boolean} isOpen is modal currently open
 * @param {string} title modal title
 * @param {ReactNode} detailsText body details text or ReactNode
 * @param {string} confirmBtnText text in confirm button
 * @param {string} cancelBtnText text in confirm button
 * @param {string} confirmBtnType="primary" type of confirm button
 * @param {string} cancelBtnType="primary" type of cancel button
 * @param {object} customStyles= jss styles
 * @param {object} customConfirmStyles= jss styles for confirm button
 * @param {object} customDetailsStyles= jss styles for details container
 * @param {object} customTitleStyles= jss styles for title container
 * @param {string} checkboxText= string value for checkbox label
 * @param {function} onCheckboxClick= handle function onClick listening onClick event of checkbox
 * @returns {ReactNode}
 */
export const ManagementModal = ({
    onClose,
    onConfirm,
    onCheckboxClick,
    isOpen,
    checkboxText,
    title,
    details = "",
    confirmBtnText,
    cancelBtnText,
    confirmBtnType = "primary",
    cancelBtnType = "primary",
    customStyles,
    customConfirmStyles,
    customDetailsStyles,
    customTitleStyles,
}: SLModalProps) => {
    const translate = useTranslation();
    const [isChecked, setIsChecked] = useState(false);

    const handleCheckboxClick = () => {
        setIsChecked(!isChecked);
    };

    const handleConfirm = () => {
        onConfirm();
        onCheckboxClick && onCheckboxClick(isChecked);
    };

    return (
        <ProModal
            shouldCloseOnOverlayClick
            customStyles={customStyles}
            isOpen={isOpen}
            showCloseIcon={false}
        >
            <WithKeysInput
                onEnter={onConfirm}
                onEscape={onClose}
            >
                <StyledSLModalContainer>
                    <StyledTitleContainer style={customTitleStyles}>{title}</StyledTitleContainer>
                    <StyledDetailsContainer style={customDetailsStyles}>{details}</StyledDetailsContainer>
                    {checkboxText ? <StyledCheckboxContainer>
                            <Checkbox
                                selected={isChecked}
                                label={checkboxText}
                                onClick={handleCheckboxClick}
                            />
                        </StyledCheckboxContainer> : null}
                    <StyledButtonContainer>
                        {cancelBtnText ? <StyledCancelBtn
                                type={cancelBtnType}
                                onClick={onClose}
                            >
                                {translate(cancelBtnText)}
                            </StyledCancelBtn> : null}
                        {confirmBtnText ? <Button
                                style={customConfirmStyles}
                                type={confirmBtnType}
                                onClick={handleConfirm}
                            >
                                {translate(confirmBtnText)}
                            </Button> : null}
                    </StyledButtonContainer>
                </StyledSLModalContainer>
            </WithKeysInput>
        </ProModal>
    );
};
